/**
 * TeamProseCollabEditor — thin wrapper that mounts the shared collaborative
 * prose editor against a Roadmap Builder *team* block room.
 *
 * Team-only. Solo prose stays on the plain editor + PATCH /blocks path; this
 * wrapper is mounted exclusively from ProseBBTab's Team-view branch.
 *
 * The Yjs room name keys off (sessionId, blockKey) via the shared helper so
 * every team member editing the same team block lands in the same room. Edit
 * vs comment-only vs read-only is derived from the viewer's role + the block's
 * lock state by `proseEditableFromRole` — the editor self-gates, so callers
 * render it for every role that reaches the block.
 */

import { forwardRef } from "react";

import {
  CollaborativeProseEditor,
  type CollaborativeProseEditorHandle,
} from "@vojomo-core/shared/src/components/collab/CollaborativeProseEditor";
import {
  buildRbBlockRoomName,
  proseEditableFromRole,
} from "@vojomo-core/shared/src/components/collab/roomName";

export interface TeamProseCollabEditorProps {
  sessionId: string;
  blockKey: "vision" | "mission";
  /** Effective viewer role (owner|advisor|team_member|solo_contributor|comment|view). */
  role: string;
  /** When the block is locked, edit access is withdrawn (comment may remain). */
  isLocked: boolean;
  /** Markdown shown read-only until the Yjs room syncs (the current team draft). */
  fallbackDraft: string;
}

/**
 * Forwards the shared editor's imperative handle so ProseBBTab can call
 * `replaceContent(markdown)` when Vo regenerates the team draft (synthesis /
 * edit-draft). The ref is the only writer to the live Yjs fragment from the
 * tab; everything else stays prop-driven.
 */
export const TeamProseCollabEditor = forwardRef<
  CollaborativeProseEditorHandle,
  TeamProseCollabEditorProps
>(function TeamProseCollabEditor(
  { sessionId, blockKey, role, isLocked, fallbackDraft },
  ref
) {
  const roomName = buildRbBlockRoomName(sessionId, blockKey);
  const { canEdit, canComment } = proseEditableFromRole(role, isLocked);

  return (
    <CollaborativeProseEditor
      ref={ref}
      roomName={roomName}
      editable={canEdit}
      canComment={canComment}
      canResolve={canComment}
      canSuggest={canComment}
      canAcceptReject={canEdit}
      fallbackContent={fallbackDraft}
      placeholder={`Write the team ${blockKey} together…`}
    />
  );
});
